import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
  CreditCard,
  Download,
  FileText,
  Check,
  AlertCircle,
  Undo2,
  Ban,
  ScrollText,
  Search,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileDown,
  RefreshCw,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Badge,
  cn,
  toast,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@cliently/ui";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { InvoiceStatus } from "@cliently/types";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { LottieIllustration } from "../components/LottieIllustration";
import receiptLottie from "../assets/lottie/receipt.json";
import {
  PromoCodeField,
  type PromoCodeValidation,
} from "../components/PromoCodeField";

type PortalInvoice = {
  id: string;
  number: string;
  amount: number;
  vatRate: number;
  vatExempt: boolean;
  status: InvoiceStatus;
  documentType?: "INVOICE" | "CREDIT_NOTE";
  creditedInvoice?: { number: string } | null;
  dueAt: string;
  paidAt: string | null;
  pdfUrl: string | null;
  createdAt: string;
  appointmentServiceItemId?: string | null;
  org?: { id: string; name: string };
};

type SortKey = "date" | "amount";
type SortDir = "asc" | "desc";

const PAGE_SIZE = 10;

const PAID_TAB_STATUSES: InvoiceStatus[] = [
  "PAID",
  "REFUNDED",
  "CANCELLED",
  "CREDIT_ISSUED",
];

const STRIPE_PARAMS = [
  "success",
  "cancelled",
  "pack",
  "sub",
  "deposit",
  "appointment",
];



const computeTtc = (inv: PortalInvoice): number => {
  const vat = inv.vatExempt ? 0 : inv.amount * (inv.vatRate / 100);
  return inv.amount + vat;
}



const formatCurrency = (amount: number, locale: string): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}



const isOverdue = (inv: PortalInvoice): boolean => {
  return inv.status === "SENT" && new Date(inv.dueAt) < new Date();
}



const downloadCsv = (invoices: PortalInvoice[], locale: string, headers: string[]) => {
  const rows = invoices.map((inv) => {
    const vat = inv.vatExempt ? 0 : inv.amount * (inv.vatRate / 100);
    return [
      inv.number,
      inv.status,
      inv.amount.toFixed(2),
      vat.toFixed(2),
      computeTtc(inv).toFixed(2),
      new Date(inv.createdAt).toLocaleDateString(locale),
      new Date(inv.dueAt).toLocaleDateString(locale),
      inv.paidAt ? new Date(inv.paidAt).toLocaleDateString(locale) : "",
    ];
  });
  const csv = [headers, ...rows].map((r) => r.join(";")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const statusIcons: Record<InvoiceStatus, typeof Check> = {
  DRAFT: FileText,
  SENT: AlertCircle,
  PAID: Check,
  REFUNDED: CreditCard,
  CANCELLED: Ban,
  CREDIT_ISSUED: ScrollText,
};

const statusVariants: Record<
  InvoiceStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  DRAFT: "secondary",
  SENT: "outline",
  PAID: "default",
  REFUNDED: "destructive",
  CANCELLED: "outline",
  CREDIT_ISSUED: "secondary",
};



const useClientInvoices = () => {
  return useQuery({
    queryKey: ["portal-invoices"],
    queryFn: async () => {
      const { data } = await api.get("/portal/invoices");
      return data as PortalInvoice[];
    },
  });
}



const useAggregateClientInvoices = () => {
  return useQuery({
    queryKey: ["portal-invoices-aggregate"],
    queryFn: async () => {
      const { data } = await api.get("/portal/aggregate/invoices");
      return data as PortalInvoice[];
    },
  });
}



const useCreateCheckout = () => {
  return useMutation({
    mutationFn: async (payload: {
      invoiceId: string;
      promoCode?: string | null;
    }) => {
      const { data } = await api.post(
        `/portal/payments/checkout/${payload.invoiceId}`,
        { promoCode: payload.promoCode ?? null },
      );
      return data as { url: string };
    },
  });
}



const useAggregateCreateCheckout = () => {
  return useMutation({
    mutationFn: async (payload: {
      invoiceId: string;
      promoCode?: string | null;
    }) => {
      const { data } = await api.post(
        `/portal/aggregate/payments/checkout/${payload.invoiceId}`,
        { promoCode: payload.promoCode ?? null },
      );
      return data as { url: string };
    },
  });
}



const useRequestRefund = () => {
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      await api.post(`/portal/invoices/${invoiceId}/refund-request`);
    },
  });
}



const useAggregateRequestRefund = () => {
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      await api.post(
        `/portal/aggregate/invoices/${invoiceId}/refund-request`,
      );
    },
  });
}



export const PaymentsPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;

  const { data: invoices, isLoading, isError, refetch } = useClientInvoices();
  const {
    data: aggregateInvoices,
    isLoading: isAggregateLoading,
    isError: isAggregateError,
    refetch: aggregateRefetch,
  } = useAggregateClientInvoices();
  const checkoutMutation = useCreateCheckout();
  const aggregateCheckoutMutation = useAggregateCreateCheckout();
  const refundRequestMutation = useRequestRefund();
  const aggregateRefundRequestMutation = useAggregateRequestRefund();

  const [refundTarget, setRefundTarget] = useState<PortalInvoice | null>(null);
  const [promoByInvoiceId, setPromoByInvoiceId] = useState<
    Record<string, PromoCodeValidation | null>
  >({});

  // --- URL-driven filter state ---
  const tab = searchParams.get("tab") === "paid" ? "paid" : "pending";
  const search = searchParams.get("q") ?? "";
  const statusFilter = (searchParams.get("status") ?? "all") as
    | "all"
    | InvoiceStatus;
  const sortKey: SortKey =
    searchParams.get("sort") === "amount" ? "amount" : "date";
  const sortDir: SortDir =
    searchParams.get("dir") === "asc" ? "asc" : "desc";
  const urlPage = Math.max(
    0,
    parseInt(searchParams.get("p") ?? "0", 10) || 0,
  );

  const setParam = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === null || v === "") next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // --- Stripe return handling ---
  const stripeHandled = useRef(false);
  useEffect(() => {
    if (stripeHandled.current) return;
    const hasStripeReturn = STRIPE_PARAMS.some((k) => searchParams.has(k));
    if (!hasStripeReturn) return;
    stripeHandled.current = true;

    const ok =
      searchParams.get("success") === "true" ||
      searchParams.get("pack") === "success" ||
      searchParams.get("sub") === "success" ||
      searchParams.get("deposit") === "success" ||
      searchParams.get("appointment") === "success";

    void queryClient.invalidateQueries({ queryKey: ["portal-invoices"] });
    void queryClient.invalidateQueries({
      queryKey: ["portal-invoices-aggregate"],
    });

    if (ok) {
      toast.success(t("payments.stripeSuccess"));
    } else {
      toast.info(t("payments.stripeCancelled"));
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of STRIPE_PARAMS) next.delete(k);
        if (ok) next.set("tab", "paid");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, queryClient, t]);

  const effectiveInvoices = isAllProsMode
    ? (aggregateInvoices ?? [])
    : (invoices ?? []);
  const effectiveIsLoading = isAllProsMode ? isAggregateLoading : isLoading;
  const effectiveIsError = isAllProsMode ? isAggregateError : isError;
  const effectiveRefetch = isAllProsMode ? aggregateRefetch : refetch;

  const invoiceStatusLabels: Record<InvoiceStatus, string> = {
    DRAFT: t("invoiceStatus.draft"),
    SENT: t("invoiceStatus.sent"),
    PAID: t("invoiceStatus.paid"),
    REFUNDED: t("invoiceStatus.refunded"),
    CANCELLED: t("invoiceStatus.cancelled"),
    CREDIT_ISSUED: t("invoiceStatus.credit_issued"),
  };

  const pending = effectiveInvoices.filter((i) => i.status === "SENT");
  const paid = effectiveInvoices.filter((i) =>
    PAID_TAB_STATUSES.includes(i.status),
  );

  const rawList = tab === "pending" ? pending : paid;

  const filteredAndSorted = useMemo(() => {
    let result = rawList;

    const needle = search.trim().toLowerCase();
    if (needle) {
      result = result.filter(
        (inv) =>
          inv.number.toLowerCase().includes(needle) ||
          inv.org?.name?.toLowerCase().includes(needle),
      );
    }

    if (tab === "paid" && statusFilter !== "all") {
      result = result.filter((inv) => inv.status === statusFilter);
    }

    return [...result].sort((a, b) => {
      const cmp =
        sortKey === "date"
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          : computeTtc(a) - computeTtc(b);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rawList, search, statusFilter, tab, sortKey, sortDir]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredAndSorted.length / PAGE_SIZE),
  );
  const page = Math.min(urlPage, totalPages - 1);
  const paginatedList = filteredAndSorted.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE,
  );

  const totalPending = pending.reduce((sum, i) => sum + computeTtc(i), 0);

  const hasActiveFilters = search !== "" || statusFilter !== "all";

  const handlePay = async (inv: PortalInvoice) => {
    try {
      const promo = promoByInvoiceId[inv.id];
      const promoCode =
        promo?.valid &&
        promo.source === "code" &&
        promo.code.trim().length > 0
          ? promo.code.trim().toUpperCase()
          : null;
      const result = isAllProsMode
        ? await aggregateCheckoutMutation.mutateAsync({
            invoiceId: inv.id,
            promoCode,
          })
        : await checkoutMutation.mutateAsync({
            invoiceId: inv.id,
            promoCode,
          });
      if (result.url) {
        window.location.href = result.url;
      }
    } catch {
      toast.error(t("payments.checkoutError"));
    }
  };

  const handleRefundConfirm = async () => {
    if (!refundTarget) return;
    try {
      if (isAllProsMode) {
        await aggregateRefundRequestMutation.mutateAsync(refundTarget.id);
      } else {
        await refundRequestMutation.mutateAsync(refundTarget.id);
      }
      toast.success(t("payments.refundRequested"));
    } catch (e: unknown) {
      const msg =
        e &&
        typeof e === "object" &&
        "response" in e &&
        e.response &&
        typeof e.response === "object" &&
        "data" in e.response &&
        e.response.data &&
        typeof e.response.data === "object" &&
        "message" in e.response.data
          ? String((e.response.data as { message?: string }).message)
          : t("payments.refundRequestError");
      toast.error(msg);
    } finally {
      setRefundTarget(null);
    }
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setParam({ dir: sortDir === "asc" ? "desc" : "asc", p: null });
    } else {
      setParam({
        sort: key === "date" ? null : key,
        dir: null,
        p: null,
      });
    }
  };

  const SortIcon = ({
    active,
    direction,
  }: {
    active: boolean;
    direction: SortDir;
  }) => {
    if (!active) return <ArrowUpDown className="ml-1 h-3 w-3" />;
    return direction === "asc" ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  };

  const csvHeaders = [
    t("payments.csvNumber"),
    t("payments.csvStatus"),
    t("payments.csvAmountHT"),
    t("payments.csvVAT"),
    t("payments.csvAmountTTC"),
    t("payments.csvCreated"),
    t("payments.csvDue"),
    t("payments.csvPaid"),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("payments.title")}
          </h1>
          <p className="text-muted-foreground">{t("payments.subtitle")}</p>
        </div>
        {effectiveInvoices.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              downloadCsv(effectiveInvoices, locale, csvHeaders)
            }
          >
            <FileDown className="mr-1 h-3.5 w-3.5" />
            {t("payments.export")}
          </Button>
        )}
      </div>

      {totalPending > 0 && (
        <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950/20">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-900 dark:text-orange-300">
                  {t("payments.pendingCount", { count: pending.length })}
                </p>
                <p className="text-sm text-orange-800 dark:text-orange-400">
                  {t("payments.total", {
                    amount: formatCurrency(totalPending, locale),
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-1 border-b">
        {(["pending", "paid"] as const).map((tabKey) => (
          <Button
            key={tabKey}
            variant="ghost"
            onClick={() =>
              setParam({
                tab: tabKey === "pending" ? null : tabKey,
                status: null,
                q: null,
                p: null,
              })
            }
            className={cn(
              "rounded-none border-b-2 px-4 py-2",
              tab === tabKey
                ? "border-primary text-primary"
                : "text-muted-foreground hover:text-foreground border-transparent",
            )}
          >
            {tabKey === "pending"
              ? t("payments.tabPending", { count: pending.length })
              : t("payments.tabPaid", { count: paid.length })}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] max-w-xs flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
          <Input
            placeholder={t("payments.searchPlaceholder")}
            value={search}
            onChange={(e) =>
              setParam({ q: e.target.value || null, p: null })
            }
            className="h-8 pl-8 text-sm"
          />
        </div>

        {tab === "paid" && (
          <Select
            value={statusFilter}
            onValueChange={(v) =>
              setParam({
                status: v === "all" ? null : v,
                p: null,
              })
            }
          >
            <SelectTrigger className="h-8 w-[160px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("payments.allStatuses")}
              </SelectItem>
              {PAID_TAB_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {invoiceStatusLabels[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 text-xs",
              sortKey === "date" && "font-semibold",
            )}
            onClick={() => toggleSort("date")}
          >
            {t("payments.sortDate")}
            <SortIcon active={sortKey === "date"} direction={sortDir} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 text-xs",
              sortKey === "amount" && "font-semibold",
            )}
            onClick={() => toggleSort("amount")}
          >
            {t("payments.sortAmount")}
            <SortIcon active={sortKey === "amount"} direction={sortDir} />
          </Button>
        </div>
      </div>

      {effectiveIsError ? (
        <Card className="flex min-h-[220px] flex-col items-center justify-center py-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <AlertCircle className="h-8 w-8 text-red-400" />
            <p className="text-muted-foreground">{t("payments.error")}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void effectiveRefetch()}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
              {t("payments.retry")}
            </Button>
          </div>
        </Card>
      ) : effectiveIsLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="bg-muted h-20 animate-pulse" />
          ))}
        </div>
      ) : paginatedList.length === 0 ? (
        <Card className="flex min-h-[220px] flex-col items-center justify-center py-8">
          <div className="text-muted-foreground flex flex-col items-center gap-3 text-center">
            {hasActiveFilters ? (
              <>
                <p>{t("payments.noResults")}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setParam({ q: null, status: null, p: null })}
                >
                  {t("payments.clearFilters")}
                </Button>
              </>
            ) : (
              <>
                <LottieIllustration
                  animationData={receiptLottie}
                  size={160}
                  className="opacity-90"
                />
                <p>
                  {tab === "pending"
                    ? t("payments.noPending")
                    : t("payments.noPaid")}
                </p>
              </>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {paginatedList.map((inv) => {
            const ttc = computeTtc(inv);
            const Icon = statusIcons[inv.status];
            const overdue = isOverdue(inv);

            return (
              <Card
                key={inv.id}
                className={cn(
                  "p-4",
                  overdue && "border-red-300 dark:border-red-800",
                )}
              >
                <div className="flex flex-col gap-4">
                {inv.status === "SENT" &&
                  (inv.documentType ?? "INVOICE") === "INVOICE" &&
                  inv.org?.id && (
                    <div className="border-border/60 bg-muted/20 space-y-2 rounded-lg border p-3">
                      <PromoCodeField
                        orgId={inv.org.id}
                        serviceItemId={inv.appointmentServiceItemId ?? null}
                        amountCents={Math.round(ttc * 100)}
                        value={promoByInvoiceId[inv.id] ?? null}
                        onChange={(value) =>
                          setPromoByInvoiceId((prev) => ({
                            ...prev,
                            [inv.id]: value,
                          }))
                        }
                      />
                      <p className="text-muted-foreground text-xs">
                        {t(
                          "payments.promoCampaignHint",
                          "Leave the field empty and tap Apply to check automatic campaign discounts; or enter a promo code.",
                        )}
                      </p>
                    </div>
                  )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        "flex h-10 w-10 items-center justify-center rounded-lg",
                        overdue
                          ? "bg-red-100 dark:bg-red-950/30"
                          : "bg-muted",
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-5 w-5",
                          overdue ? "text-red-500" : "text-primary",
                        )}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{inv.number}</span>
                        <Badge variant={statusVariants[inv.status]}>
                          {invoiceStatusLabels[inv.status]}
                        </Badge>
                        {overdue && (
                          <Badge variant="destructive" className="gap-1">
                            <Clock className="h-3 w-3" />
                            {t("payments.overdue")}
                          </Badge>
                        )}
                        {inv.org?.name && (
                          <Badge variant="secondary">{inv.org.name}</Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground text-xs">
                        {new Date(inv.createdAt).toLocaleDateString(locale)}
                        {inv.paidAt &&
                          ` · ${t("payments.paidOn", { date: new Date(inv.paidAt).toLocaleDateString(locale) })}`}
                        {inv.status === "SENT" &&
                          ` · ${t("payments.dueOn", { date: new Date(inv.dueAt).toLocaleDateString(locale) })}`}
                      </p>
                      {inv.documentType === "CREDIT_NOTE" &&
                        inv.creditedInvoice?.number && (
                          <p className="text-muted-foreground text-xs">
                            {t("payments.creditNoteFor", {
                              number: inv.creditedInvoice.number,
                            })}
                          </p>
                        )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3">
                    <div className="text-right">
                      <p className="font-semibold">
                        {formatCurrency(ttc, locale)}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {inv.vatExempt
                          ? t("payments.vatExempt")
                          : t("payments.htBreakdown", {
                              ht: formatCurrency(inv.amount, locale),
                              rate: inv.vatRate,
                            })}
                      </p>
                    </div>

                    {inv.status === "SENT" &&
                      (inv.documentType ?? "INVOICE") === "INVOICE" && (
                        <Button
                          size="sm"
                          onClick={() => handlePay(inv)}
                          disabled={
                            isAllProsMode
                              ? aggregateCheckoutMutation.isPending
                              : checkoutMutation.isPending
                          }
                        >
                          <CreditCard className="mr-1 h-3 w-3" />
                          {t("payments.pay")}
                        </Button>
                      )}

                    {inv.status === "PAID" &&
                      (inv.documentType ?? "INVOICE") === "INVOICE" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-destructive/40 text-destructive hover:bg-destructive/10"
                          disabled={
                            isAllProsMode
                              ? aggregateRefundRequestMutation.isPending
                              : refundRequestMutation.isPending
                          }
                          onClick={() => setRefundTarget(inv)}
                        >
                          <Undo2 className="mr-1 h-3 w-3" />
                          {t("payments.requestRefund")}
                        </Button>
                      )}

                    {inv.pdfUrl && (
                      <Button size="sm" variant="outline" asChild>
                        <a
                          href={inv.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download className="h-3 w-3" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() =>
              setParam({ p: page - 1 === 0 ? null : String(page - 1) })
            }
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-muted-foreground text-sm">
            {t("payments.page", { current: page + 1, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages - 1}
            onClick={() => setParam({ p: String(page + 1) })}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Dialog
        open={!!refundTarget}
        onOpenChange={(open) => !open && setRefundTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("payments.refundDialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("payments.refundDialogDescription", {
                number: refundTarget?.number,
                amount: refundTarget
                  ? formatCurrency(computeTtc(refundTarget), locale)
                  : "",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTarget(null)}>
              {t("payments.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRefundConfirm}
              disabled={
                isAllProsMode
                  ? aggregateRefundRequestMutation.isPending
                  : refundRequestMutation.isPending
              }
            >
              {t("payments.confirmRefund")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
